import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import "./Elections.css";
import "./ElectionEnded.css";

const emptyElection = () => ({
  title: "",
  position: "",
  description: "",
  level: "branch",
  electing_body: "branch_general_meeting",
  ballot_type: "single_choice",
  seats: 1,
  notice_date: "",
  branch_id: "",
  nomination_starts: "",
  nomination_ends: "",
  voting_starts: "",
  voting_ends: "",
});
const workflow = {
  draft: ["open-nominations", "Open nominations"],
  nominations: ["start-verification", "Start verification"],
  verification: ["open-voting", "Open voting"],
  voting: ["close-voting", "Close voting"],
  results_approved: ["publish-results", "Publish results"],
};
const dateText = (value) =>
  value ? new Date(value).toLocaleString("en-TZ") : "—";
const candidatePhotoUrl = (photo) =>
  photo
    ? photo.startsWith("http")
      ? photo
      : `${api.baseUrl.replace(/\/api\/?$/, "")}/uploads/members/${photo}`
    : null;

export default function Elections() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { t } = useLanguage();
  const manage =
    hasPermission("elections.manage") || hasPermission("elections.create");
  const [elections, setElections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [branches, setBranches] = useState([]);
  const [metadata, setMetadata] = useState({
    members: [],
    committee_members: [],
    selected_member_ids: [],
    reviews: [],
    eligibility_note: "",
  });
  const [form, setForm] = useState(emptyElection());
  const [committee, setCommittee] = useState({ chair_member_id: "", secretary_member_id: "", member_ids: [] });
  const [showForm, setShowForm] = useState(false);
  const [manifesto, setManifesto] = useState("");
  const [candidateEligibility, setCandidateEligibility] = useState({
    tra_employee: false,
    not_management: false,
    no_criminal_conviction: false,
    no_disciplinary_guilt: false,
    contributions_12_months: false,
    term_limit_ok: false,
    applying_one_position: false,
    professional_qualification: "",
    leadership_experience: "",
  });
  const [candidateId, setCandidateId] = useState("");
  const [voteChoice, setVoteChoice] = useState("");
  const [resultData, setResultData] = useState(null);
  const [handover, setHandover] = useState({
    winner_candidate_id: "",
    outgoing_officer: "",
    handover_date: "",
    documents_notes: "",
    handover_notes: "",
    completed: false,
  });
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({
    review_type: "election_conduct",
    candidate_id: "",
    grounds: "",
    evidence_notes: "",
  });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [clock, setClock] = useState(Date.now());
  const electionEnded = (election) => election && (new Date(election.voting_ends).getTime() <= clock || ["closed", "results_approved", "published"].includes(election.status));

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [e, b] = await Promise.all([
        api.getElections(),
        api.getBranchOptions(),
      ]);
      setElections(e.data || []);
      setBranches(b.data || []);
    } catch (x) {
      setError(x.message);
    } finally {
      setLoading(false);
    }
  }, []);
  const loadDetail = useCallback(
    async (electionId) => {
      setLoading(true);
      setError("");
      try {
        const [r, reviewResponse, m] = await Promise.all([
          api.getElection(electionId),
          api.getElectionReviews(electionId),
          manage ? api.getElectionMetadata(electionId) : Promise.resolve(null),
        ]);
        setSelected(r.data);
        const appointed = r.data.committee || [];
        setCommittee({ chair_member_id: String(appointed.find(x => x.committee_role === "chair")?.member_id || ""), secretary_member_id: String(appointed.find(x => x.committee_role === "secretary")?.member_id || ""), member_ids: appointed.filter(x => x.committee_role === "member").map(x => Number(x.member_id)) });
        setReviews(reviewResponse.data || []);
        if (m) setMetadata(m.data);
        if (manage || r.data.can_approve_results || r.data.status === "published") {
          try {
            const result = await api.getElectionResults(electionId);
            setResultData(result.data);
          } catch (_) {
            setResultData(null);
          }
        } else setResultData(null);
      } catch (x) {
        setError(x.message);
      } finally {
        setLoading(false);
      }
    },
    [manage],
  );
  useEffect(() => {
    id ? loadDetail(id) : loadList();
  }, [id, loadDetail, loadList]);
  useEffect(() => {
    if (selected?.status === "voting" && !manage) {
      const timer = setTimeout(
        () =>
          document
            .querySelector(".ballot-paper")
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        120,
      );
      return () => clearTimeout(timer);
    }
  }, [selected?.status, manage]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.createElection(form);
      setForm(emptyElection());
      setShowForm(false);
      setNotice("Election draft created.");
      await loadList();
    } catch (x) {
      setError(x.message);
    }
  };
  const transition = async (action) => {
    if (
      action === "open-nominations" &&
      metadata.selected_member_ids.length === 0
    ) {
      setError(
        "First select at least one active, fully-paid voter and click Save voter register.",
      );
      document
        .querySelector(".delegate-register")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (
      ["close-voting", "cancel"].includes(action) &&
      !window.confirm(`Confirm ${action.replace("-", " ")}?`)
    )
      return;
    setError("");
    try {
      await api.transitionElection(selected.id, action);
      setNotice("Election workflow updated.");
      await loadDetail(selected.id);
    } catch (x) {
      setError(x.message);
    }
  };
  const apply = async (e) => {
    e.preventDefault();
    try {
      await api.applyCandidate(selected.id, {
        manifesto,
        ...candidateEligibility,
      });
      setManifesto("");
      setNotice("Nomination submitted for verification.");
      await loadDetail(selected.id);
    } catch (x) {
      setError(x.message);
    }
  };
  const decide = async (candidate, action) => {
    let reason = null;
    if (action === "reject") {
      reason = window.prompt("Rejection reason:");
      if (!reason) return;
    }
    try {
      await api.decideElectionCandidate(
        selected.id,
        candidate.id,
        action,
        reason,
      );
      await loadDetail(selected.id);
    } catch (x) {
      setError(x.message);
    }
  };
  const vote = async () => {
    const single = approved.length === 1;
    const effectiveId = single ? String(approved[0].id) : candidateId;
    if (!effectiveId || (single && !voteChoice)) return;
    if (!window.confirm("Submit this vote? You cannot change it afterwards."))
      return;
    try {
      await api.vote(
        selected.id,
        effectiveId,
        "",
        single ? voteChoice : "candidate",
      );
      setNotice("Your vote has been recorded securely.");
      setCandidateId("");
      setVoteChoice("");
      await loadDetail(selected.id);
    } catch (x) {
      setError(x.message);
    }
  };
  const toggleDelegate = (memberId) =>
    setMetadata((current) => ({
      ...current,
      selected_member_ids: current.selected_member_ids.includes(
        Number(memberId),
      )
        ? current.selected_member_ids.filter((id) => id !== Number(memberId))
        : [...current.selected_member_ids, Number(memberId)],
    }));
  const saveDelegates = async () => {
    const voterIds = metadata.selected_member_ids
      .map(Number)
      .filter(Number.isInteger);
    if (!voterIds.length) {
      setError("Select at least one eligible voter before saving.");
      return;
    }
    setError("");
    try {
      const response = await api.setElectionEligibleVoters(
        selected.id,
        voterIds,
      );
      setNotice(
        `${response.data?.saved_count ?? voterIds.length} eligible voter(s) saved successfully.`,
      );
      await loadDetail(selected.id);
    } catch (x) {
      setError(x.message);
    }
  };
  const saveCommittee = async () => {
    try { const response = await api.saveElectionCommittee(selected.id, committee); setNotice(response.message); await loadDetail(selected.id); }
    catch (x) { setError(x.message); }
  };
  const toggleCommitteeMember = memberId => setCommittee(current => ({ ...current, member_ids: current.member_ids.includes(Number(memberId)) ? current.member_ids.filter(x => x !== Number(memberId)) : [...current.member_ids, Number(memberId)] }));
  const saveHandover = async (e) => {
    e.preventDefault();
    try {
      await api.saveElectionHandover(selected.id, handover);
      setNotice("Handover record saved.");
    } catch (x) {
      setError(x.message);
    }
  };
  const submitReview = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.submitElectionReview(selected.id, reviewForm);
      setReviewForm({
        review_type: "election_conduct",
        candidate_id: "",
        grounds: "",
        evidence_notes: "",
      });
      setNotice("Review request submitted to the Election Committee.");
      await loadDetail(selected.id);
    } catch (x) {
      setError(x.message);
    }
  };
  const decideReview = async (review, status) => {
    const decision = window.prompt(
      status === "upheld"
        ? "Written decision and corrective action:"
        : "Written reasons for dismissal:",
    );
    if (!decision) return;
    try {
      await api.decideElectionReview(selected.id, review.id, {
        status,
        decision,
      });
      setNotice("Committee decision recorded.");
      await loadDetail(selected.id);
    } catch (x) {
      setError(x.message);
    }
  };

  if (!id)
    return (
      <div className="elections-page">
        <div className="elections-header election-admin-header">
          <div>
            <h1 className="page-title">Elections</h1>
            <p className="page-subtitle">
              Nomination, verification, voting and publication of union leaders.
            </p>
          </div>
          {manage && (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(!showForm)}
            >
              {showForm ? "Close" : "+ Create election"}
            </button>
          )}
        </div>
        {error && <div className="election-alert error">{error}</div>}
        {notice && <div className="election-alert success">{notice}</div>}
        {showForm && (
          <form className="election-create-panel" onSubmit={create}>
            <input
              required
              placeholder="Election title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <input
              required
              placeholder="Leadership position"
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            />
            <select
              value={form.level}
              onChange={(e) =>
                setForm({
                  ...form,
                  level: e.target.value,
                  branch_id:
                    e.target.value === "national" ? "" : form.branch_id,
                  electing_body:
                    e.target.value === "national"
                      ? "national_general_meeting"
                      : "branch_general_meeting",
                })
              }
            >
              <option value="branch">Branch level</option>
              <option value="national">National level</option>
            </select>
            <select
              value={form.electing_body}
              onChange={(e) =>
                setForm({ ...form, electing_body: e.target.value })
              }
            >
              {form.level === "branch" ? (
                <option value="branch_general_meeting">
                  Branch General Meeting
                </option>
              ) : (
                <>
                  <option value="national_general_meeting">
                    National General Meeting
                  </option>
                  <option value="national_council">National Council</option>
                </>
              )}
            </select>
            <select
              value={form.ballot_type}
              onChange={(e) =>
                setForm({ ...form, ballot_type: e.target.value })
              }
            >
              <option value="single_choice">Candidate choice</option>
            </select>
            <input
              type="number"
              min="1"
              value={form.seats}
              onChange={(e) => setForm({ ...form, seats: e.target.value })}
              placeholder="Number of seats"
            />
            <select
              required={form.level === "branch"}
              disabled={form.level !== "branch"}
              value={form.branch_id}
              onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
            >
              <option value="">Select branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.name}
                </option>
              ))}
            </select>
            <label>
              Election notice date
              <input
                type="date"
                value={form.notice_date}
                onChange={(e) =>
                  setForm({ ...form, notice_date: e.target.value })
                }
              />
            </label>
            <textarea
              placeholder="Election description and approved rules"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
            <label>
              Nomination opens
              <input
                required
                type="datetime-local"
                value={form.nomination_starts}
                onChange={(e) =>
                  setForm({ ...form, nomination_starts: e.target.value })
                }
              />
            </label>
            <label>
              Nomination closes
              <input
                required
                type="datetime-local"
                value={form.nomination_ends}
                onChange={(e) =>
                  setForm({ ...form, nomination_ends: e.target.value })
                }
              />
            </label>
            <label>
              Voting opens
              <input
                required
                type="datetime-local"
                value={form.voting_starts}
                onChange={(e) =>
                  setForm({ ...form, voting_starts: e.target.value })
                }
              />
            </label>
            <label>
              Voting closes
              <input
                required
                type="datetime-local"
                value={form.voting_ends}
                onChange={(e) =>
                  setForm({ ...form, voting_ends: e.target.value })
                }
              />
            </label>
            <button className="btn btn-primary">Save draft</button>
          </form>
        )}
        {loading ? (
          <p>Loading elections…</p>
        ) : (
          <div className="elections-grid">
            {elections.map((e) => (
              <article className="election-card" key={e.id}>
                <div className="election-card-header">
                  <span className={`status-badge status-${e.status} ${electionEnded(e) && e.status !== "published" ? "election-ended-blink" : ""}`}>
                    {electionEnded(e) && e.status !== "published" ? "Election ended" : e.status.replaceAll("_", " ")}
                  </span>
                  <span>{e.branch_name || "National"}</span>
                </div>
                <h3>{e.title}</h3>
                <p className="election-position">{e.position}</p>
                <div className="election-stats">
                  <span>Eligible: {e.eligible_voter_count}</span>
                  <span>Candidates: {e.candidate_count}</span>
                  <span>Votes: {e.votes_cast}</span>
                </div>
                <button
                  className="btn btn-primary btn-block"
                  onClick={() => navigate(`/elections/${e.id}${e.status === "published" ? "#results" : ""}`)}
                >
                  {e.status === "published" ? "View results" : electionEnded(e) ? "Election ended" : "Open election"}
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    );

  if (loading && !selected)
    return <div className="elections-page">Loading election…</div>;
  if (!selected)
    return (
      <div className="elections-page">{error || "Election not found."}</div>
    );
  const approved = (selected.candidates || []).filter(
    (c) => c.status === "approved",
  );
  const results = resultData || selected.results;
  return (
    <div className="elections-page">
      <button
        className="btn btn-secondary"
        onClick={() => navigate("/elections")}
      >
        ← Elections
      </button>
      {error && <div className="election-alert error">{error}</div>}
      {notice && <div className="election-alert success">{notice}</div>}
      {electionEnded(selected) && (
        <div className={`election-ended-banner ${selected.status === "published" ? "published" : ""}`}>
          <span className={selected.status === "published" ? "" : "blink-dot"} />
          <div><b>Election ended</b><small>Voting closed automatically when the scheduled voting time ended.</small></div>
          {selected.status === "published" && <button className="btn btn-primary" onClick={() => document.getElementById("election-results")?.scrollIntoView({behavior:"smooth"})}>View results</button>}
        </div>
      )}
      <div className="election-detail-card">
        {(manage || selected.status !== "voting") && (
          <>
            <header className="card-header">
              <div>
                <span className="election-eyebrow">
                  {selected.branch_name || "NATIONAL ELECTION"}
                </span>
                <h2>{selected.title}</h2>
                <p>{selected.position}</p>
              </div>
              <span className={`status-badge status-${selected.status}`}>
                {selected.status.replaceAll("_", " ")}
              </span>
            </header>
            <div className="election-timeline">
              <div>
                <b>Nominations</b>
                <span>
                  {dateText(selected.nomination_starts)} →{" "}
                  {dateText(selected.nomination_ends)}
                </span>
              </div>
              <div>
                <b>Voting</b>
                <span>
                  {dateText(selected.voting_starts)} →{" "}
                  {dateText(selected.voting_ends)}
                </span>
              </div>
              <div>
                <b>Eligibility snapshot</b>
                <span>{selected.eligible_voter_count} active members</span>
              </div>
              <div>
                <b>Turnout</b>
                <span>{selected.votes_cast} votes cast</span>
              </div>
            </div>
            {selected.description && (
              <section className="section">
                <h4>Election rules / description</h4>
                <p>{selected.description}</p>
              </section>
            )}
          </>
        )}
        {(manage || selected.can_verify_results || selected.can_approve_results) && (
          <section className="section election-control">
            <h4>Election administration</h4>
            <div>
              {workflow[selected.status] && (workflow[selected.status][0] !== "approve-results" || selected.can_approve_results) && (
                <button
                  className="btn btn-primary"
                  onClick={() => transition(workflow[selected.status][0])}
                >
                  {workflow[selected.status][1]}
                </button>
              )}
              {selected.status === "closed" && !selected.results_verified_at && selected.can_verify_results && <button className="btn btn-primary" onClick={() => transition("verify-results")}>Verify results</button>}
              {selected.status === "closed" && selected.results_verified_at && selected.can_approve_results && <button className="btn btn-primary" onClick={() => transition("approve-results")}>Approve results</button>}
              {selected.status === "closed" && <span className="election-approval-state">{selected.results_verified_at ? "Results verified by Committee Secretary · awaiting Chairperson approval" : "Awaiting verification by Committee Secretary"}</span>}
              {manage && !["published", "cancelled"].includes(selected.status) && (
                <button
                  className="btn btn-danger"
                  onClick={() => transition("cancel")}
                >
                  Cancel election
                </button>
              )}
            </div>
            <small>
              Opening nominations freezes the list of eligible active members.
              Results are approved only by the appointed Election Committee Chairperson before publication.
            </small>
          </section>
        )}
        {manage && ["draft", "closed"].includes(selected.status) && (
          <section className="section election-committee">
            <div className="section-header"><h4>Election Committee</h4><b>{(selected.committee || []).length} appointed</b></div>
            <p>Appoint active members with system accounts. Only the Chairperson can approve the final results.</p>
            <div className="committee-officers">
              <label>Committee Chairperson<select value={committee.chair_member_id} onChange={e => setCommittee({...committee,chair_member_id:e.target.value})}><option value="">Select chairperson</option>{(metadata.committee_members || []).map(m=><option key={m.id} value={m.id}>{m.name} — {m.member_number}</option>)}</select></label>
              <label>Committee Secretary<select value={committee.secretary_member_id} onChange={e => setCommittee({...committee,secretary_member_id:e.target.value})}><option value="">Select secretary</option>{(metadata.committee_members || []).map(m=><option key={m.id} value={m.id}>{m.name} — {m.member_number}</option>)}</select></label>
            </div>
            <h5>Other committee members</h5>
            <div className="delegate-grid">{(metadata.committee_members || []).filter(m=>![String(committee.chair_member_id),String(committee.secretary_member_id)].includes(String(m.id))).map(m=><label key={m.id}><input type="checkbox" checked={committee.member_ids.includes(Number(m.id))} onChange={()=>toggleCommitteeMember(m.id)}/><span><b>{m.name}</b><small>{m.member_number} · {m.branch_name}</small></span></label>)}</div>
            <button className="btn btn-primary" disabled={!committee.chair_member_id||!committee.secretary_member_id} onClick={saveCommittee}>Save Election Committee</button>
          </section>
        )}
        {!manage && (selected.committee || []).length > 0 && <section className="section election-committee"><div className="section-header"><h4>Election Committee</h4></div><div className="committee-roster">{selected.committee.map(person=><div key={person.id}><b>{person.name}</b><span>{person.committee_role}</span></div>)}</div></section>}
        {manage && selected.status === "draft" && (
          <section className="section delegate-register">
            <div className="section-header">
              <h4>Eligible voters — active and fully paid</h4>
              <b>{metadata.selected_member_ids.length} selected</b>
            </div>
            <p>
              {metadata.eligibility_note ||
                "Only active members who have paid every monthly contribution through the previous month are listed."}
            </p>
            <div className="delegate-grid">
              {metadata.members.map((member) => (
                <label key={member.id}>
                  <input
                    type="checkbox"
                    checked={metadata.selected_member_ids.includes(
                      Number(member.id),
                    )}
                    onChange={() => toggleDelegate(member.id)}
                  />
                  <span>
                    <b>{member.name}</b>
                    <small>
                      {member.member_number} · {member.branch_name} · Fully paid
                    </small>
                  </span>
                </label>
              ))}
            </div>
            {metadata.members.length === 0 && (
              <div className="election-alert error">
                No active fully-paid members were found in this election scope.
              </div>
            )}
            <button
              className="btn btn-primary"
              disabled={!metadata.selected_member_ids.length}
              onClick={saveDelegates}
            >
              Save voter register
            </button>
          </section>
        )}
        {(manage || selected.status !== "voting") && (
          <section className="section">
            <div className="section-header">
              <h4>Candidates</h4>
              <span>{approved.length} approved</span>
            </div>
            <div className="candidates-grid">
              {(selected.candidates || []).map((c) => (
                <article className="candidate-card" key={c.id}>
                  <div className="candidate-avatar">
                    {candidatePhotoUrl(c.member_photo) ? (
                      <img
                        src={candidatePhotoUrl(c.member_photo)}
                        alt={c.name}
                      />
                    ) : (
                      c.name?.charAt(0)
                    )}
                  </div>
                  <div className="candidate-info">
                    <h5>{c.name}</h5>
                    <span className={`candidate-state ${c.status}`}>
                      {c.status}
                    </span>
                    <p>{c.manifesto}</p>
                    {c.rejection_reason && (
                      <small>Reason: {c.rejection_reason}</small>
                    )}
                  </div>
                  {manage && c.status === "pending" && (
                    <div className="candidate-actions">
                      <button onClick={() => decide(c, "approve")}>
                        Approve
                      </button>
                      <button onClick={() => decide(c, "reject")}>
                        Reject
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
        {selected.status === "nominations" && !manage && (
          <section className="section nominate-section">
            <h4>Apply as candidate</h4>
            {!selected.can_apply_as_candidate ? (
              <div className="election-alert error">
                Only an active member within this election's branch or scope can
                apply as a candidate.
              </div>
            ) : !selected.my_candidate_status ? (
              <form className="nominate-form" onSubmit={apply}>
                <textarea
                  required
                  rows="4"
                  placeholder="Your manifesto"
                  value={manifesto}
                  onChange={(e) => setManifesto(e.target.value)}
                />
                <div className="candidate-declarations">
                  {[
                    ["tra_employee", "I am a TRA employee"],
                    ["not_management", "I am not part of management"],
                    ["no_criminal_conviction", "I have no criminal conviction"],
                    [
                      "no_disciplinary_guilt",
                      "I have no employer disciplinary guilt",
                    ],
                    [
                      "contributions_12_months",
                      "I paid monthly contributions continuously for at least 12 months",
                    ],
                    [
                      "term_limit_ok",
                      "I have not served two consecutive terms in this same position",
                    ],
                    [
                      "applying_one_position",
                      "I am applying for only one leadership position",
                    ],
                  ].map(([key, label]) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        checked={candidateEligibility[key]}
                        onChange={(e) =>
                          setCandidateEligibility({
                            ...candidateEligibility,
                            [key]: e.target.checked,
                          })
                        }
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <input
                  placeholder="Professional qualifications (required for Secretary General/Treasurer)"
                  value={candidateEligibility.professional_qualification}
                  onChange={(e) =>
                    setCandidateEligibility({
                      ...candidateEligibility,
                      professional_qualification: e.target.value,
                    })
                  }
                />
                <textarea
                  placeholder="Union leadership experience"
                  value={candidateEligibility.leadership_experience}
                  onChange={(e) =>
                    setCandidateEligibility({
                      ...candidateEligibility,
                      leadership_experience: e.target.value,
                    })
                  }
                />
                <button className="btn btn-primary">Submit nomination</button>
              </form>
            ) : (
              <p>Your nomination status: {selected.my_candidate_status}</p>
            )}
          </section>
        )}
        {selected.status === "nominations" && manage && (
          <section className="section election-control">
            <h4>Nominations are open to members</h4>
            <p>
              Members can now sign in and submit candidate applications.
              Administrators review applications in the Candidates section and
              then start verification.
            </p>
          </section>
        )}
        {selected.status === "voting" && !manage && (
          <section className="section voting-section ballot-paper">
            <div className="ballot-heading">
              <div className="ballot-seal">TU</div>
              <div>
                <span>{t("OFFICIAL BALLOT PAPER")}</span>
                <h4>{selected.title}</h4>
                <p>
                  {selected.position} · {selected.branch_name || "National"}
                </p>
              </div>
              <b>{t("SECRET")}</b>
            </div>
            {selected.has_voted ? (
              <div className="vote-form ballot-body completed-ballot">
                <div className="ballot-instruction vote-complete-note">
                  <b>✓ {t("Your vote has been received")}</b>
                  <span>
                    {t(
                      "All candidates are shown below. Your selection cannot be changed.",
                    )}
                  </span>
                </div>
                <div className="ballot-options">
                  {approved.map((candidate, index) => {
                    const chosen =
                      Number(selected.my_vote?.candidate_id) ===
                      Number(candidate.id);
                    return (
                      <button
                        type="button"
                        disabled
                        key={candidate.id}
                        className={chosen ? "selected receipt-selected" : ""}
                      >
                        <span className="ballot-number">{index + 1}</span>
                        <span className="candidate-avatar">
                          {candidatePhotoUrl(candidate.member_photo) ? (
                            <img
                              src={candidatePhotoUrl(candidate.member_photo)}
                              alt={candidate.name}
                            />
                          ) : (
                            candidate.name?.charAt(0)
                          )}
                        </span>
                        <span className="ballot-candidate">
                          <b>{candidate.name}</b>
                          <small>
                            {candidate.manifesto || t("Approved candidate")}
                          </small>
                          {chosen && (
                            <strong className="my-vote-label">
                              ✓ {t("You selected this candidate")}
                              {selected.my_vote?.choice &&
                              selected.my_vote.choice !== "candidate"
                                ? ` — ${selected.my_vote.choice.toUpperCase()}`
                                : ""}
                            </strong>
                          )}
                        </span>
                        <span className="ballot-mark">{chosen ? "✓" : ""}</span>
                      </button>
                    );
                  })}
                </div>
                {!selected.my_vote?.candidate_id && (
                  <div className="voted-message historical-vote-note">
                    {t(
                      "The selection for this earlier vote cannot be displayed because it was cast before vote receipts were introduced.",
                    )}
                  </div>
                )}
                {selected.my_vote?.voted_at && (
                  <small className="vote-submitted-at">
                    {t("Vote submitted")} {dateText(selected.my_vote.voted_at)}
                  </small>
                )}
              </div>
            ) : !selected.my_eligibility ? (
              <div className="ballot-ineligible">
                <b>{t("You are not on the voter register.")}</b>
                <span>
                  {t(
                    "Contact the Election Committee if you believe this is an error.",
                  )}
                </span>
              </div>
            ) : (
              <div className="vote-form ballot-body">
                <div className="ballot-instruction">
                  <b>{t("Mark one choice")}</b>
                  <span>
                    {t("Voting closes")} {dateText(selected.voting_ends)}
                  </span>
                </div>
                {approved.length === 1 ? (
                  <>
                    <div className="single-candidate">
                      <div className="candidate-avatar">
                        {candidatePhotoUrl(approved[0].member_photo) ? (
                          <img
                            src={candidatePhotoUrl(approved[0].member_photo)}
                            alt={approved[0].name}
                          />
                        ) : (
                          approved[0].name?.charAt(0)
                        )}
                      </div>
                      <div>
                        <h5>{approved[0].name}</h5>
                        <p>{approved[0].manifesto}</p>
                      </div>
                    </div>
                    <p className="ballot-question">
                      {t("Do you approve this candidate?")}
                    </p>
                    <div className="yes-no-ballot">
                      <button
                        type="button"
                        className={voteChoice === "yes" ? "selected" : ""}
                        onClick={() => setVoteChoice("yes")}
                      >
                        <i>✓</i>
                        <span>
                          {t("YES")}<small>{t("Approve")}</small>
                        </span>
                      </button>
                      <button
                        type="button"
                        className={voteChoice === "no" ? "selected no" : ""}
                        onClick={() => setVoteChoice("no")}
                      >
                        <i>×</i>
                        <span>
                          {t("NO")}<small>{t("Reject")}</small>
                        </span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="ballot-options">
                    {approved.map((candidate, index) => (
                      <button
                        type="button"
                        key={candidate.id}
                        className={
                          candidateId === String(candidate.id) ? "selected" : ""
                        }
                        onClick={() => setCandidateId(String(candidate.id))}
                      >
                        <span className="ballot-number">{index + 1}</span>
                        <span className="candidate-avatar">
                          {candidatePhotoUrl(candidate.member_photo) ? (
                            <img
                              src={candidatePhotoUrl(candidate.member_photo)}
                              alt={candidate.name}
                            />
                          ) : (
                            candidate.name?.charAt(0)
                          )}
                        </span>
                        <span className="ballot-candidate">
                          <b>{candidate.name}</b>
                          <small>
                            {candidate.manifesto || t("Approved candidate")}
                          </small>
                        </span>
                        <span className="ballot-mark">
                          {candidateId === String(candidate.id) ? "✓" : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="ballot-confirm">
                  <button
                    disabled={
                      approved.length === 1 ? !voteChoice : !candidateId
                    }
                    className="btn btn-primary"
                    onClick={vote}
                  >
                    {t("Submit my vote")}
                  </button>
                  <small>
                    {t("Your vote cannot be changed after submission.")}
                  </small>
                </div>
              </div>
            )}
          </section>
        )}
        {results && (
          <section className="section results-section" id="election-results">
            <div className="section-header results-heading">
              <div>
                <span className="results-kicker">{t("OFFICIAL RESULTS")}</span>
                <h4>
                {results.published
                    ? t("Published election results")
                    : t("Provisional results — administrators only")}
                </h4>
                <small>{selected.position}</small>
              </div>
              <div className="results-total">
                <strong>{results.total_ballots ?? results.total_votes}</strong>
                <span>{t("Total ballots")}</span>
              </div>
            </div>
            {results.ballot_type === "yes_no" && (
              <div className="yes-no-summary">
                <b>{t("YES")}: {results.yes_votes}</b>
                <b>{t("NO")}: {results.no_votes}</b>
              </div>
            )}
            <div className="tls-results-list">
              {results.rows.map((r, index) => {
                const candidate = approved.find(
                  (item) => Number(item.id) === Number(r.candidate_id),
                );
                const rankColors = [
                  { bar: "#15945f", soft: "#e4f7ed", text: "#0b6240" },
                  { bar: "#e4bf00", soft: "#fff7c7", text: "#756100" },
                  { bar: "#3685d6", soft: "#e8f2ff", text: "#1f5f9e" },
                  { bar: "#e98635", soft: "#fff0e3", text: "#9a4e12" },
                  { bar: "#8b6bc2", soft: "#f1ebfa", text: "#5f438e" },
                ];
                const rankColor = rankColors[index] || {
                  bar: "#7a8580",
                  soft: "#edf0ee",
                  text: "#4f5a55",
                };
                return (
                  <article
                    className={`result-bar-item ${index === 0 ? "result-leader" : ""}`}
                    key={r.candidate_id}
                    style={{
                      "--result-color": rankColor.bar,
                      "--result-soft": rankColor.soft,
                      "--result-text": rankColor.text,
                    }}
                  >
                    <div className="result-rank">{index + 1}</div>
                    <div className="result-avatar candidate-avatar">
                      {candidatePhotoUrl(candidate?.member_photo) ? (
                        <img
                          src={candidatePhotoUrl(candidate.member_photo)}
                          alt={r.name}
                        />
                      ) : (
                        r.name?.charAt(0)
                      )}
                    </div>
                    <div className="result-content">
                      <div className="result-info">
                        <div>
                          <b>{r.name}</b>
                          {r.is_winner && (
                            <span className="winner-mark">{t("Winner")}</span>
                          )}
                        </div>
                        <span>
                          <strong>{r.votes}</strong> {t("votes")} · {r.percentage}%
                        </span>
                      </div>
                      <div className="result-bar-bg">
                        <div
                          className="result-bar-fill"
                          style={{ width: `${r.percentage}%` }}
                        />
                      </div>
                    </div>
                    {results.published && r.is_winner && manage && (
                      <button
                        className="btn btn-secondary result-certificate"
                        onClick={() =>
                          api
                            .printElectionCertificate(selected.id, r.candidate_id)
                            .catch((x) => setError(x.message))
                        }
                      >
                        {t("Print winner certificate")}
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
            <div className="results-pdf-actions">
              <button
                className="btn btn-secondary"
                onClick={() =>
                  api
                    .previewElectionResultsPdf(selected.id)
                    .catch((x) => setError(x.message))
                }
              >
                {t("Preview PDF")}
              </button>
              <button
                className="btn btn-primary"
                onClick={() =>
                  api
                    .downloadElectionResultsPdf(selected.id)
                    .catch((x) => setError(x.message))
                }
              >
                {t("Download TFM PDF")}
              </button>
            </div>
          </section>
        )}
        {manage && selected.status === "published" && results && (
          <section className="section">
            <h4>Leadership handover</h4>
            <form className="handover-form" onSubmit={saveHandover}>
              <select
                required
                value={handover.winner_candidate_id}
                onChange={(e) =>
                  setHandover({
                    ...handover,
                    winner_candidate_id: e.target.value,
                  })
                }
              >
                <option value="">Select declared winner</option>
                {results.rows
                  .filter((r) => r.is_winner)
                  .map((r) => (
                    <option key={r.candidate_id} value={r.candidate_id}>
                      {r.name}
                    </option>
                  ))}
              </select>
              <input
                required
                placeholder="Outgoing officer name"
                value={handover.outgoing_officer}
                onChange={(e) =>
                  setHandover({ ...handover, outgoing_officer: e.target.value })
                }
              />
              <input
                required
                type="date"
                value={handover.handover_date}
                onChange={(e) =>
                  setHandover({ ...handover, handover_date: e.target.value })
                }
              />
              <textarea
                placeholder="Documents and assets handed over"
                value={handover.documents_notes}
                onChange={(e) =>
                  setHandover({ ...handover, documents_notes: e.target.value })
                }
              />
              <textarea
                placeholder="Handover notes"
                value={handover.handover_notes}
                onChange={(e) =>
                  setHandover({ ...handover, handover_notes: e.target.value })
                }
              />
              <label>
                <input
                  type="checkbox"
                  checked={handover.completed}
                  onChange={(e) =>
                    setHandover({ ...handover, completed: e.target.checked })
                  }
                />{" "}
                Handover completed and signed
              </label>
              <button className="btn btn-primary">Save handover</button>
            </form>
          </section>
        )}
        {!["draft", "cancelled"].includes(selected.status) &&
          (manage || selected.my_candidate_status) && (
            <details className="review-dropdown">
              <summary>
                <span>
                  {manage
                    ? "Candidate reviews and complaints"
                    : "Election reviews and complaints"}
                </span>
                <small>
                  {manage ? "Election Committee desk" : "For candidates only"}
                </small>
              </summary>
              <section className="section review-section">
                {!manage && (
                  <p className="review-deadline-note">
                    Result reviews close 7 days after publication.
                  </p>
                )}
                {!manage && (
                  <form className="review-form" onSubmit={submitReview}>
                    <select
                      value={reviewForm.review_type}
                      onChange={(e) =>
                        setReviewForm({
                          ...reviewForm,
                          review_type: e.target.value,
                          candidate_id: "",
                        })
                      }
                    >
                      <option value="election_conduct">
                        Election conduct complaint
                      </option>
                      <option value="candidate_disqualification">
                        Candidate qualification review
                      </option>
                      {selected.status === "published" && (
                        <option value="result">Result review</option>
                      )}
                    </select>
                    {reviewForm.review_type ===
                      "candidate_disqualification" && (
                      <select
                        required
                        value={reviewForm.candidate_id}
                        onChange={(e) =>
                          setReviewForm({
                            ...reviewForm,
                            candidate_id: e.target.value,
                          })
                        }
                      >
                        <option value="">Select candidate</option>
                        {(selected.candidates || []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <textarea
                      required
                      placeholder="Grounds for the review or complaint"
                      value={reviewForm.grounds}
                      onChange={(e) =>
                        setReviewForm({
                          ...reviewForm,
                          grounds: e.target.value,
                        })
                      }
                    />
                    <textarea
                      placeholder="Evidence, witnesses or document references"
                      value={reviewForm.evidence_notes}
                      onChange={(e) =>
                        setReviewForm({
                          ...reviewForm,
                          evidence_notes: e.target.value,
                        })
                      }
                    />
                    <button className="btn btn-primary">
                      Submit to Election Committee
                    </button>
                  </form>
                )}
                {manage && reviews.length === 0 && (
                  <div className="election-alert">
                    No candidate reviews or complaints have been submitted.
                  </div>
                )}
                {reviews.length > 0 && (
                  <div className="review-list">
                    {reviews.map((review) => (
                      <article key={review.id}>
                        <div>
                          <b>{review.review_type.replaceAll("_", " ")}</b>
                          <span className={`review-status ${review.status}`}>
                            {review.status.replaceAll("_", " ")}
                          </span>
                        </div>
                        {manage && (
                          <small>Applicant: {review.applicant_name}</small>
                        )}
                        {review.candidate_name && (
                          <small>Candidate: {review.candidate_name}</small>
                        )}
                        <p>{review.grounds}</p>
                        {review.evidence_notes && (
                          <small>Evidence: {review.evidence_notes}</small>
                        )}
                        {review.decision && (
                          <div className="review-decision">
                            <b>Committee decision</b>
                            <p>{review.decision}</p>
                          </div>
                        )}
                        {manage && review.status === "submitted" && (
                          <div className="candidate-actions">
                            <button
                              onClick={() => decideReview(review, "upheld")}
                            >
                              Uphold
                            </button>
                            <button
                              onClick={() => decideReview(review, "dismissed")}
                            >
                              Dismiss
                            </button>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </details>
          )}
      </div>
    </div>
  );
}
